import * as React from "react";
import { Component, Fragment } from "react";
import { CommandBarButton, DatePicker, Icon, MessageBarType } from "office-ui-fabric-react";

import { connect } from "react-redux";
import { IRootState } from "../../store";
import DispatchProps from "../../utils/DispatchProps";
import ApiService from "../ApiService";
import Actions from "../../redux/Actions";
import H from "history";
import moment from 'moment';


import UserSelector from "../UserSelector";
import GroupSelector, { IGroupOption } from "../GroupSelector";
import DropIn from '../../models/DropIn';
import Table, { TableColumn } from "../Table";
import TableCell from "../TableCell";
import { UserOption } from "../UserSelectorBase";
import LoadingPage from "../LoadingPage";
import SchoolMenu from "../SchoolMenu";
import SchoolPageTitle from "../SchoolPageTitle";

interface CoverManagerPageProps {
    tenantId: string;
    schoolCode: string;
    apiService: ApiService;
    history: H.History;
}

interface CoverManagerPageState {
    sdsSchoolId?: string;
    selectedUsers: UserOption[];
    selectedGroups: IGroupOption[];
    selectedStartDate: Date;
    selectedEndDate: Date;
    saveButtonDisabled: boolean;
    coverTableRows?: DropIn[];
}

class CoverManagerPage extends Component<CoverManagerPageProps & DispatchProps, CoverManagerPageState> {

    constructor(props: CoverManagerPageProps & DispatchProps) {
        super(props);

        this.state = {
            selectedUsers: [],
            selectedGroups: [],
            selectedStartDate: moment().toDate(),
            selectedEndDate: moment().add(1, "day").toDate(),
            saveButtonDisabled: false
        };
    }

    private static readonly columns: TableColumn[] = [
        {
            title: "Teacher",
            propertyPath: "userEmail",
            flex: 2,
            extraDetail: "userId",
            enableSort: true
        },
        {
            title: "Class",
            propertyPath: "targetGroupDisplayName",
            flex: 2,
            extraDetail: "targetGroupId",
            enableSort: true
        },
        {
            title: "Start Date",
            propertyPath: "startDate",
            flex: 1,
            type: "date",
            enableSort: true
        },
        {
            title: "Removal Date",
            propertyPath: "endDate",
            flex: 1,
            type: "date",
            enableSort: true
        },
        {
            title: "Status",
            propertyPath: "status",
            flex: 1,
        }
    ];

    private cellForColumn = (item: DropIn, column: TableColumn, index: number) => {
        const value = item[column.propertyPath];
        if (column.propertyPath === "userEmail") {
            return (
                <TableCell flex={column.flex}>
                    <span>{value}</span>
                    <span className={'cdb-table__extra-detail'}>{item.userId}</span>
                </TableCell>
            );
        }
        if (column.propertyPath === "targetGroupDisplayName") {
            return (
                <TableCell flex={column.flex}>
                    <span>{value}</span>
                    <span className={'cdb-table__extra-detail'}>{item.targetGroupId}</span>
                </TableCell>
            );
        }
        if (column.propertyPath === "startDate" || column.propertyPath === "endDate") {
            return (
                <TableCell flex={column.flex}>
                    <span>{moment(value).format("LL")}</span>
                </TableCell>
            );
        }
        if (column.propertyPath === "status") {

            let pillColour: string = "";
            let pillText: string = "";
            if (item.addSuccess) {
                //DropIn added
                if (moment(item.endDate).isBefore(moment())) {
                    pillColour = "amber";
                    pillText = "Pending Removal";
                } else {
                    pillColour = "amber";
                    pillText = "Added to Class";
                }
            }
            else {
                pillColour = "grey";
                pillText = "Waiting";
            }
            if (item.removeSuccess) {
                pillColour = "green";
                pillText = "Complete";
            }

            return (
                <TableCell flex={column.flex}>
                    <div className={`pill pill--${pillColour} pill--small`}>{pillText}</div>
                </TableCell>
            );
        }

    }

    async componentDidMount(): Promise<void> {
        if (this.props.tenantId) {
            await this.load();
        }
    }

    async componentDidUpdate(prevProps: Readonly<CoverManagerPageProps & DispatchProps>, prevState: any): Promise<void> {

        if (this.props.tenantId !== prevProps.tenantId || this.props.schoolCode !== prevProps.schoolCode) {
            await this.load();
        }
    }

    private async load(): Promise<void> {
        const dropIns = await this.props.apiService.getDropIns(this.props.tenantId, this.props.schoolCode);

        const school = await this.props.apiService.getSchool(this.props.tenantId, this.props.schoolCode);

        this.setState({
            coverTableRows: dropIns,
            sdsSchoolId: school.sdsSchoolId
        });
    }

    handleValidation() {
        if (this.state.selectedUsers === undefined || this.state.selectedUsers.length == 0) {
            throw 'Teacher Selector cannot be empty';
        }
        if (this.state.selectedGroups === undefined || this.state.selectedGroups.length == 0) {
            throw 'Class Selector cannot be empty';
        }
    }

    private submitDropIn = async () => {
        try {
            this.handleValidation();
            this.setState({ saveButtonDisabled: true });
            
            const userId = this.state.selectedUsers[0].id
            const userEmail = this.state.selectedUsers[0].userPrincipalName;
            const startDate = moment(this.state.selectedStartDate).format();
            const endDate = moment(this.state.selectedEndDate).format();
            
            const dropIns: DropIn[] = this.state.selectedGroups.map(groupOption => {
                return {
                    tenantId: this.props.tenantId,
                    schoolCode: this.props.schoolCode,
                    
                    userId: userId,
                    userEmail: userEmail,
                    startDate: startDate,
                    endDate: endDate,

                    targetGroupId: groupOption.group?.id!,
                    targetGroupDisplayName: groupOption.group?.displayName!,
                };
            });

            await this.props.apiService.postDropIns(dropIns);
            this.props.dispatch(Actions.showMessageBar("Drop in request successful", MessageBarType.success));

            this.setState({
                selectedUsers: [],
                selectedGroups: [],
                selectedStartDate: moment().toDate(),
                selectedEndDate: moment().add(1, "day").toDate()
            })
            await this.load();
        }
        catch (e) {
            let message = `Drop in Request Failed: ${e.message}`;
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(message, MessageBarType.error));
        }
        finally {
            this.setState({
                saveButtonDisabled: false
            });
        }
    };

    private renderContent() {
        if (this.state.sdsSchoolId === undefined || this.state.coverTableRows === undefined) {
            return <LoadingPage />;
        }

        return (
            <Fragment>
                <div className={"cdb-admin-content cdb-admin-content--column"}>
                    <div className={"flex-row flex--justify-content-center p-left-1rem p-right-1rem p-top-1rem"}>
                        <div className="help-box-item help-box-item--wide">
                            <div className="help-box-item__icon">
                                <Icon iconName="PeopleRepeat" className="" />
                            </div>
                            <span className="help-box-item__title">Class Team Drop in</span>
                            <span className="help-box-item__description">
                                <span>Complete the form below to temporarily assign a teacher to classes. The teacher will be removed automatically on the selected date.</span>
                                <br />
                                <br />
                                <span>Please note that teachers assigned to classes may also get added to subject and department teams too, they will not automatically be removed from these teams.</span>
                            </span>
                        </div>
                    </div>
        
                    <div className="card card">
                        <div className="card__header">
                            <span className={"card__title"}>Add Drop in</span>
                            <CommandBarButton
                                iconProps={{ iconName: "save" }}
                                text="Save"
                                disabled={this.state.saveButtonDisabled}
                                onClick={() => this.submitDropIn()} />
                        </div>
                        <div className={"card__content"}>
                            <UserSelector
                                tenantId={this.props.tenantId}
                                className="card__input"
                                disabled={false}
                                label={"Teacher (select one)"}
                                multiSelect={false}
                                additionalFilter={["Teacher", "Staff"]}
                                selectedUsers={this.state.selectedUsers}
                                onSelectionChanged={(selectedUsers: UserOption[]) => {
                                    this.setState({
                                        selectedUsers
                                    });
                                }} />
                            <GroupSelector
                                tenantId={this.props.tenantId}
                                className="card__input"
                                disabled={this.state.sdsSchoolId === undefined}
                                multiSelect={true}
                                sectionFilter={true}
                                sdsSchoolId={this.state.sdsSchoolId}
                                label={"Add to classes (up to 25)"}
                                selectedOptions={this.state.selectedGroups}
                                onSelectionChanged={(selectedGroups: IGroupOption[]) => {
                                    this.setState({
                                        selectedGroups
                                    });
                                }} />
                            <DatePicker
                                className={"filter-controls__input"}
                                label={"When should this teacher be added to the class?"}
                                value={this.state.selectedStartDate}
                                minDate={moment().toDate()}
                                onSelectDate={(selectedDate?: Date | undefined | null) => {
                                    const date = moment(selectedDate!).utc(true).toDate();
                                    this.setState({
                                        selectedStartDate: date
                                    });
                                }}
                                placeholder="Select a date..."
                                ariaLabel="Select a date"
                            />
                            <DatePicker
                                className={"filter-controls__input"}
                                label={"When should this teacher be removed from the class?"}
                                value={this.state.selectedEndDate}
                                minDate={moment().add(1, "day").toDate()}
                                onSelectDate={(selectedDate?: Date | undefined | null) => {
                                    const date = moment(selectedDate!).utc(true).toDate();
                                    this.setState({
                                        selectedEndDate: date
                                    });
                                }}
                                placeholder="Select a date..."
                                ariaLabel="Select a date"
                            />
                        </div>
                    </div>
                    <Table
                        title={"Existing Drop ins"}
                        columns={CoverManagerPage.columns}
                        items={this.state.coverTableRows ? this.state.coverTableRows : []}
                        enableSort={true}
                        cellForColumn={this.cellForColumn}
                    //rowPropsForItem={this.rowPropsForItem}
                    ></Table>
                </div>
            </Fragment>
        );
    }

    public render(): JSX.Element {
        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title="Class Drop in" reverse={true} />
                <SchoolMenu prefix="Class Drop in" />
                {this.renderContent()}
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: any): CoverManagerPageProps => {
        return {
            tenantId: ownProps.match.params.tenantId,
            schoolCode: ownProps.match.params.schoolCode,
            apiService: state.auth.apiService,
            history: ownProps.history
        };

    }
)(CoverManagerPage)